import { ModuleInfo, FuncTypeInfo, TypeInfo, ImportInfo, FunctionInfo } from '../BinaryReader';
import OpCodes from '../OpCodes';
import type { OpCodeDef } from '../types';
import { BasicBlock, ControlFlowGraph } from './ControlFlowGraph';
import { immediateNumbers } from '../InstructionDecoder';
import type { DecodedInstruction } from '../InstructionDecoder';
import { SsaVariable, SsaValue, SsaInstr, SsaBlock, SsaFunction, CallTarget } from './SsaTypes';

// ─── Opcode lookup tables built from OpCodes ───

const BINARY_OPS = new Map<OpCodeDef, string>([
  [OpCodes.i32_add, '+'], [OpCodes.i64_add, '+'], [OpCodes.f32_add, '+'], [OpCodes.f64_add, '+'],
  [OpCodes.i32_sub, '-'], [OpCodes.i64_sub, '-'], [OpCodes.f32_sub, '-'], [OpCodes.f64_sub, '-'],
  [OpCodes.i32_mul, '*'], [OpCodes.i64_mul, '*'], [OpCodes.f32_mul, '*'], [OpCodes.f64_mul, '*'],
  [OpCodes.i32_div_s, '/'], [OpCodes.i64_div_s, '/'], [OpCodes.f32_div, '/'], [OpCodes.f64_div, '/'],
  [OpCodes.i32_div_u, '/u'], [OpCodes.i64_div_u, '/u'],
  [OpCodes.i32_rem_s, '%'], [OpCodes.i64_rem_s, '%'], [OpCodes.i32_rem_u, '%u'], [OpCodes.i64_rem_u, '%u'],
  [OpCodes.i32_and, '&'], [OpCodes.i64_and, '&'], [OpCodes.i32_or, '|'], [OpCodes.i64_or, '|'],
  [OpCodes.i32_xor, '^'], [OpCodes.i64_xor, '^'],
  [OpCodes.i32_shl, '<<'], [OpCodes.i64_shl, '<<'],
  [OpCodes.i32_shr_s, '>>'], [OpCodes.i64_shr_s, '>>'], [OpCodes.i32_shr_u, '>>>'], [OpCodes.i64_shr_u, '>>>'],
  [OpCodes.i32_rotl, 'rotl'], [OpCodes.i64_rotl, 'rotl'], [OpCodes.i32_rotr, 'rotr'], [OpCodes.i64_rotr, 'rotr'],
  [OpCodes.f32_min, 'min'], [OpCodes.f64_min, 'min'], [OpCodes.f32_max, 'max'], [OpCodes.f64_max, 'max'],
  [OpCodes.f32_copysign, 'copysign'], [OpCodes.f64_copysign, 'copysign'],
]);

const COMPARE_OPS = new Map<OpCodeDef, string>([
  [OpCodes.i32_eq, '=='], [OpCodes.i64_eq, '=='], [OpCodes.f32_eq, '=='], [OpCodes.f64_eq, '=='],
  [OpCodes.i32_ne, '!='], [OpCodes.i64_ne, '!='], [OpCodes.f32_ne, '!='], [OpCodes.f64_ne, '!='],
  [OpCodes.i32_lt_s, '<'], [OpCodes.i64_lt_s, '<'], [OpCodes.f32_lt, '<'], [OpCodes.f64_lt, '<'],
  [OpCodes.i32_lt_u, '<u'], [OpCodes.i64_lt_u, '<u'],
  [OpCodes.i32_gt_s, '>'], [OpCodes.i64_gt_s, '>'], [OpCodes.f32_gt, '>'], [OpCodes.f64_gt, '>'],
  [OpCodes.i32_gt_u, '>u'], [OpCodes.i64_gt_u, '>u'],
  [OpCodes.i32_le_s, '<='], [OpCodes.i64_le_s, '<='], [OpCodes.f32_le, '<='], [OpCodes.f64_le, '<='],
  [OpCodes.i32_le_u, '<=u'], [OpCodes.i64_le_u, '<=u'],
  [OpCodes.i32_ge_s, '>='], [OpCodes.i64_ge_s, '>='], [OpCodes.f32_ge, '>='], [OpCodes.f64_ge, '>='],
  [OpCodes.i32_ge_u, '>=u'], [OpCodes.i64_ge_u, '>=u'],
]);

const UNARY_OPS = new Map<OpCodeDef, string>([
  [OpCodes.i32_clz, 'clz'], [OpCodes.i64_clz, 'clz'], [OpCodes.i32_ctz, 'ctz'], [OpCodes.i64_ctz, 'ctz'],
  [OpCodes.i32_popcnt, 'popcnt'], [OpCodes.i64_popcnt, 'popcnt'],
  [OpCodes.f32_abs, 'abs'], [OpCodes.f64_abs, 'abs'], [OpCodes.f32_neg, 'neg'], [OpCodes.f64_neg, 'neg'],
  [OpCodes.f32_ceil, 'ceil'], [OpCodes.f64_ceil, 'ceil'], [OpCodes.f32_floor, 'floor'], [OpCodes.f64_floor, 'floor'],
  [OpCodes.f32_trunc, 'trunc'], [OpCodes.f64_trunc, 'trunc'],
  [OpCodes.f32_nearest, 'nearest'], [OpCodes.f64_nearest, 'nearest'],
  [OpCodes.f32_sqrt, 'sqrt'], [OpCodes.f64_sqrt, 'sqrt'],
  [OpCodes.i32_extend8_s, 'extend8_s'], [OpCodes.i32_extend16_s, 'extend16_s'],
  [OpCodes.i64_extend8_s, 'extend8_s'], [OpCodes.i64_extend16_s, 'extend16_s'], [OpCodes.i64_extend32_s, 'extend32_s'],
]);

const CONVERSION_OPS = new Map<OpCodeDef, string>([
  [OpCodes.i32_wrap_i64, '(int)'], [OpCodes.i64_extend_i32_s, '(long)'], [OpCodes.i64_extend_i32_u, '(unsigned long)'],
  [OpCodes.f32_convert_i32_s, '(float)'], [OpCodes.f32_convert_i32_u, '(float)(unsigned)'],
  [OpCodes.f32_convert_i64_s, '(float)'], [OpCodes.f32_convert_i64_u, '(float)(unsigned long)'],
  [OpCodes.f64_convert_i32_s, '(double)'], [OpCodes.f64_convert_i32_u, '(double)(unsigned)'],
  [OpCodes.f64_convert_i64_s, '(double)'], [OpCodes.f64_convert_i64_u, '(double)(unsigned long)'],
  [OpCodes.i32_trunc_f32_s, '(int)'], [OpCodes.i32_trunc_f32_u, '(unsigned)'],
  [OpCodes.i32_trunc_f64_s, '(int)'], [OpCodes.i32_trunc_f64_u, '(unsigned)'],
  [OpCodes.i64_trunc_f32_s, '(long)'], [OpCodes.i64_trunc_f64_s, '(long)'],
  [OpCodes.f32_demote_f64, '(float)'], [OpCodes.f64_promote_f32, '(double)'],
  [OpCodes.i32_reinterpret_f32, 'reinterpret_i32'], [OpCodes.i64_reinterpret_f64, 'reinterpret_i64'],
  [OpCodes.f32_reinterpret_i32, 'reinterpret_f32'], [OpCodes.f64_reinterpret_i64, 'reinterpret_f64'],
  [OpCodes.i32_trunc_sat_f32_s, '(int)'], [OpCodes.i32_trunc_sat_f32_u, '(unsigned)'],
  [OpCodes.i32_trunc_sat_f64_s, '(int)'], [OpCodes.i32_trunc_sat_f64_u, '(unsigned)'],
  [OpCodes.i64_trunc_sat_f32_s, '(long)'], [OpCodes.i64_trunc_sat_f32_u, '(unsigned long)'],
  [OpCodes.i64_trunc_sat_f64_s, '(long)'], [OpCodes.i64_trunc_sat_f64_u, '(unsigned long)'],
]);

// ─── Helpers ───

function wasmTypeStr(valueType: number | { name: string }): string {
  if (typeof valueType === 'object' && 'name' in valueType) {
    return valueType.name;
  }
  const names: Record<number, string> = {
    [-1]: 'i32', [-2]: 'i64', [-3]: 'f32', [-4]: 'f64', [-5]: 'v128',
    0x7f: 'i32', 0x7e: 'i64', 0x7d: 'f32', 0x7c: 'f64', 0x7b: 'v128',
  };
  return names[valueType] || 'i32';
}

function flattenTypes(moduleInfo: ModuleInfo): TypeInfo[] {
  const flat: TypeInfo[] = [];
  for (const typeEntry of moduleInfo.types) {
    if (typeEntry.kind === 'rec') {
      for (const inner of typeEntry.types) { flat.push(inner); }
    } else {
      flat.push(typeEntry);
    }
  }
  return flat;
}

function resultTypeFromOpCode(opCode: OpCodeDef): string {
  return opCode.mnemonic.split('.')[0];
}

/**
 * Constructs SSA form from the CFG by simulating the WASM operand stack per block.
 * Processes blocks in reverse-postorder. At merge points, phi nodes are inserted
 * for both stack slots and local variables. Loop headers use a two-pass approach:
 * phis are created with placeholder inputs on the first pass, then patched with
 * actual back-edge values after all loop body blocks have been processed.
 */
export function buildSsa(
  cfg: ControlFlowGraph,
  moduleInfo: ModuleInfo,
  localFuncIndex: number,
): SsaFunction {
  const func = moduleInfo.functions[localFuncIndex];
  const funcTypeRaw = flattenTypes(moduleInfo)[func.typeIndex];
  if (!funcTypeRaw || funcTypeRaw.kind !== 'func') {
    return { blocks: [], variables: [], paramCount: 0, localCount: 0, entryBlockId: 0, exitBlockId: 0, intrinsicNames: new Map() };
  }
  return new SsaConstruction(cfg, moduleInfo, localFuncIndex, funcTypeRaw).build();
}

/**
 * Builds SSA for one function by simulating the WASM operand stack over the CFG
 * in reverse-postorder, inserting phi nodes at merge points and back edges. The
 * operand stack and local versions are per-block state (reset in processBlock);
 * everything else (variable counter, phi worklist, exit snapshots) persists.
 */
class SsaConstruction {
  private readonly importedFunctions: ImportInfo[];
  private readonly importedGlobals: ImportInfo[];
  private readonly importedFuncCount: number;
  private readonly func: FunctionInfo;
  private readonly flatTypes: TypeInfo[];
  private readonly paramCount: number;
  private readonly totalLocalCount: number;

  private variableCounter = 0;
  private voidIntrinsicCounter = CallTarget.FirstVoidIntrinsic;
  private readonly allVariables: SsaVariable[] = [];
  private readonly intrinsicNames = new Map<number, string>();
  private readonly ssaBlocks: SsaBlock[];
  private readonly localVersions = new Map<number, Map<number, SsaVariable>>();
  private readonly stackAtExit = new Map<number, SsaValue[]>();
  private readonly blockOrder: BasicBlock[];
  private readonly processedBlocks = new Set<number>();
  private readonly incompletePhis: { phi: SsaInstr & { kind: 'phi' }; blockId: number; localIndex: number }[] = [];

  // Per-block state, reset at the start of each processBlock.
  private stack: SsaValue[] = [];
  private currentLocals = new Map<number, SsaVariable>();
  private ssaBlock!: SsaBlock;
  private cfgBlock!: BasicBlock;

  constructor(
    private readonly cfg: ControlFlowGraph,
    private readonly moduleInfo: ModuleInfo,
    localFuncIndex: number,
    private readonly funcType: FuncTypeInfo,
  ) {
    this.importedFunctions = moduleInfo.imports.filter(imp => imp.kind === 0);
    this.importedGlobals = moduleInfo.imports.filter(imp => imp.kind === 3);
    this.importedFuncCount = this.importedFunctions.length;
    this.func = moduleInfo.functions[localFuncIndex];
    this.flatTypes = flattenTypes(moduleInfo);
    this.paramCount = funcType.parameterTypes.length;
    let totalLocalCount = this.paramCount;
    for (const localGroup of this.func.locals) {
      totalLocalCount += localGroup.count;
    }
    this.totalLocalCount = totalLocalCount;

    this.ssaBlocks = cfg.blocks.map(cfgBlock => ({
      id: cfgBlock.id,
      instructions: [],
      successors: cfgBlock.successors.map(successor => successor.id),
      predecessors: cfgBlock.predecessors.map(predecessor => predecessor.id),
    }));
    for (const block of cfg.blocks) {
      this.localVersions.set(block.id, new Map());
    }
    this.initEntryLocals();
    this.blockOrder = computeReversePostOrder(cfg);
  }

  build(): SsaFunction {
    for (const cfgBlock of this.blockOrder) {
      this.processBlock(cfgBlock);
    }
    this.patchIncompletePhis();
    return {
      blocks: this.ssaBlocks,
      variables: this.allVariables,
      paramCount: this.paramCount,
      localCount: this.totalLocalCount,
      entryBlockId: this.cfg.entry.id,
      exitBlockId: this.cfg.exit.id,
      intrinsicNames: this.intrinsicNames,
    };
  }

  private newVariable(name: string, type: string, blockId: number): SsaVariable {
    const variable: SsaVariable = { id: this.variableCounter++, name, type, definedInBlock: blockId };
    this.allVariables.push(variable);
    return variable;
  }

  private getLocalType(localIndex: number): string {
    if (localIndex < this.paramCount) {
      return wasmTypeStr(this.funcType.parameterTypes[localIndex]);
    }
    let offset = this.paramCount;
    for (const localGroup of this.func.locals) {
      if (localIndex < offset + localGroup.count) {
        return wasmTypeStr(localGroup.type);
      }
      offset += localGroup.count;
    }
    return 'i32';
  }

  private getGlobalType(globalIndex: number): string {
    const importedGlobalCount = this.importedGlobals.length;
    if (globalIndex >= importedGlobalCount && globalIndex - importedGlobalCount < this.moduleInfo.globals.length) {
      return wasmTypeStr(this.moduleInfo.globals[globalIndex - importedGlobalCount].valueType);
    }
    if (globalIndex < importedGlobalCount) {
      const globalImport = this.importedGlobals[globalIndex];
      if (globalImport?.globalType) {
        return wasmTypeStr(globalImport.globalType.valueType);
      }
    }
    return 'i32';
  }

  private getCallTargetType(targetIndex: number): FuncTypeInfo | null {
    if (targetIndex < this.importedFuncCount) {
      const importEntry = this.importedFunctions[targetIndex];
      if (importEntry?.typeIndex !== undefined && importEntry.typeIndex < this.flatTypes.length) {
        const typeEntry = this.flatTypes[importEntry.typeIndex];
        if (typeEntry.kind === 'func') { return typeEntry; }
      }
      return null;
    }
    const localIdx = targetIndex - this.importedFuncCount;
    if (localIdx < this.moduleInfo.functions.length) {
      const typeEntry = this.flatTypes[this.moduleInfo.functions[localIdx].typeIndex];
      if (typeEntry && typeEntry.kind === 'func') { return typeEntry; }
    }
    return null;
  }

  private initEntryLocals(): void {
    const entryLocals = this.localVersions.get(this.cfg.entry.id)!;
    for (let localIndex = 0; localIndex < this.paramCount; localIndex++) {
      entryLocals.set(localIndex, this.newVariable(`param${localIndex}`, this.getLocalType(localIndex), this.cfg.entry.id));
    }
    for (let localIndex = this.paramCount; localIndex < this.totalLocalCount; localIndex++) {
      entryLocals.set(localIndex, this.newVariable(`local${localIndex}`, this.getLocalType(localIndex), this.cfg.entry.id));
    }
  }

  private processBlock(cfgBlock: BasicBlock): void {
    this.cfgBlock = cfgBlock;
    this.ssaBlock = this.ssaBlocks[cfgBlock.id];
    this.stack = [];
    const hasUnprocessedPredecessor = cfgBlock.predecessors.some(pred => !this.processedBlocks.has(pred.id));
    this.inheritStack(cfgBlock, hasUnprocessedPredecessor);
    this.currentLocals = this.inheritLocals(cfgBlock, hasUnprocessedPredecessor);
    for (const instruction of cfgBlock.instructions) {
      this.translateInstruction(instruction);
    }
    this.appendImplicitReturn(cfgBlock);
    this.recordExitState(cfgBlock);
  }

  private inheritStack(cfgBlock: BasicBlock, hasUnprocessedPredecessor: boolean): void {
    if (cfgBlock.predecessors.length === 1 && !hasUnprocessedPredecessor) {
      const predStack = this.stackAtExit.get(cfgBlock.predecessors[0].id);
      if (predStack) { this.stack.push(...predStack); }
      return;
    }
    if (cfgBlock.predecessors.length <= 1) {
      return;
    }
    const processedPreds = cfgBlock.predecessors.filter(pred => this.processedBlocks.has(pred.id));
    const predStacks = processedPreds.map(pred => this.stackAtExit.get(pred.id) || []);
    const maxDepth = Math.max(0, ...predStacks.map(predStack => predStack.length));
    for (let slotIndex = 0; slotIndex < maxDepth; slotIndex++) {
      const values = predStacks.map(predStack => slotIndex < predStack.length ? predStack[slotIndex] : null).filter((value): value is SsaValue => value !== null);
      if (values.length === 0) { continue; }
      this.stack.push(this.mergeStackSlot(cfgBlock, slotIndex, values, hasUnprocessedPredecessor));
    }
  }

  private mergeStackSlot(cfgBlock: BasicBlock, slotIndex: number, values: SsaValue[], hasUnprocessedPredecessor: boolean): SsaValue {
    const slotType = values[0].type;
    if (!hasUnprocessedPredecessor) {
      const allSameVar = values.every(value => 'id' in value && !('kind' in value) && value.id === (values[0] as SsaVariable).id);
      if (allSameVar) {
        return values[0];
      }
    }
    const phiVar = this.newVariable(`stack_${slotIndex}_b${cfgBlock.id}`, slotType, cfgBlock.id);
    const phiInstr: SsaInstr & { kind: 'phi' } = {
      kind: 'phi', result: phiVar,
      inputs: cfgBlock.predecessors.map(pred => ({ blockId: pred.id, value: (this.stackAtExit.get(pred.id) || [])[slotIndex] || values[0] })),
    };
    this.ssaBlock.instructions.push(phiInstr);
    if (hasUnprocessedPredecessor) {
      // Loop-header phi: back-edge inputs are patched after the body is processed.
      this.incompletePhis.push({ phi: phiInstr, blockId: cfgBlock.id, localIndex: -(slotIndex + 1) });
    }
    return phiVar;
  }

  private inheritLocals(cfgBlock: BasicBlock, hasUnprocessedPredecessor: boolean): Map<number, SsaVariable> {
    const currentLocals = new Map<number, SsaVariable>(this.localVersions.get(cfgBlock.id) || new Map());
    if (cfgBlock.predecessors.length === 1 && !hasUnprocessedPredecessor) {
      const predLocals = this.localVersions.get(cfgBlock.predecessors[0].id);
      if (predLocals) {
        for (const [localIndex, variable] of predLocals) {
          if (!currentLocals.has(localIndex)) { currentLocals.set(localIndex, variable); }
        }
      }
      return currentLocals;
    }
    if (cfgBlock.predecessors.length <= 1) {
      return currentLocals;
    }
    for (const localIndex of this.localIndicesFromPredecessors(cfgBlock)) {
      this.mergeLocal(cfgBlock, localIndex, hasUnprocessedPredecessor, currentLocals);
    }
    return currentLocals;
  }

  private localIndicesFromPredecessors(cfgBlock: BasicBlock): Set<number> {
    const allLocalIndices = new Set<number>();
    for (const predecessor of cfgBlock.predecessors) {
      const predLocals = this.localVersions.get(predecessor.id);
      if (predLocals) {
        for (const localIndex of predLocals.keys()) { allLocalIndices.add(localIndex); }
      }
    }
    return allLocalIndices;
  }

  private mergeLocal(cfgBlock: BasicBlock, localIndex: number, hasUnprocessedPredecessor: boolean, currentLocals: Map<number, SsaVariable>): void {
    const localOf = (predId: number): SsaVariable | undefined => (this.localVersions.get(predId) || new Map()).get(localIndex);
    if (hasUnprocessedPredecessor) {
      const knownVersions = cfgBlock.predecessors.filter(pred => this.processedBlocks.has(pred.id)).map(pred => localOf(pred.id)).filter((version): version is SsaVariable => version !== undefined);
      if (knownVersions.length === 0) { return; }
      const phiVar = this.newVariable(`phi_${localIndex}_b${cfgBlock.id}`, knownVersions[0].type, cfgBlock.id);
      const phiInstr: SsaInstr & { kind: 'phi' } = { kind: 'phi', result: phiVar, inputs: cfgBlock.predecessors.map(pred => ({ blockId: pred.id, value: localOf(pred.id) || knownVersions[0] })) };
      this.ssaBlock.instructions.push(phiInstr);
      currentLocals.set(localIndex, phiVar);
      this.incompletePhis.push({ phi: phiInstr, blockId: cfgBlock.id, localIndex });
      return;
    }
    const versions = cfgBlock.predecessors.map(pred => localOf(pred.id)).filter((version): version is SsaVariable => version !== undefined);
    if (versions.length === 0) { return; }
    if (versions.every(version => version.id === versions[0].id)) {
      currentLocals.set(localIndex, versions[0]);
      return;
    }
    const phiVar = this.newVariable(`phi_${localIndex}_b${cfgBlock.id}`, versions[0].type, cfgBlock.id);
    this.ssaBlock.instructions.push({ kind: 'phi', result: phiVar, inputs: cfgBlock.predecessors.map(pred => ({ blockId: pred.id, value: localOf(pred.id) || versions[0] })) });
    currentLocals.set(localIndex, phiVar);
  }

  private appendImplicitReturn(cfgBlock: BasicBlock): void {
    const hasExitEdge = cfgBlock.successors.some(successor => successor.id === this.cfg.exit.id);
    if (hasExitEdge && this.stack.length > 0 && this.funcType.returnTypes.length > 0) {
      const returnValues: SsaValue[] = [];
      for (let retIdx = 0; retIdx < this.funcType.returnTypes.length && this.stack.length > 0; retIdx++) {
        const value = this.stack.pop();
        if (value) { returnValues.unshift(value); }
      }
      this.ssaBlock.instructions.push({ kind: 'return', values: returnValues });
    } else if (hasExitEdge && this.funcType.returnTypes.length === 0) {
      const lastInstr = this.ssaBlock.instructions[this.ssaBlock.instructions.length - 1];
      if (!lastInstr || lastInstr.kind !== 'return') {
        this.ssaBlock.instructions.push({ kind: 'return', values: [] });
      }
    }
  }

  private recordExitState(cfgBlock: BasicBlock): void {
    this.localVersions.set(cfgBlock.id, this.currentLocals);
    this.stackAtExit.set(cfgBlock.id, [...this.stack]);
    this.processedBlocks.add(cfgBlock.id);
    for (const successor of cfgBlock.successors) {
      let successorLocals = this.localVersions.get(successor.id);
      if (!successorLocals) {
        successorLocals = new Map();
        this.localVersions.set(successor.id, successorLocals);
      }
      if (successorLocals.size === 0) {
        for (const [localIndex, variable] of this.currentLocals) {
          successorLocals.set(localIndex, variable);
        }
      }
    }
  }

  private patchIncompletePhis(): void {
    for (const { phi, blockId, localIndex } of this.incompletePhis) {
      const block = this.cfg.blocks.find(candidate => candidate.id === blockId);
      if (!block) { continue; }
      for (let inputIndex = 0; inputIndex < phi.inputs.length; inputIndex++) {
        const predId = block.predecessors[inputIndex].id;
        if (localIndex < 0) {
          const stackSlot = -(localIndex + 1);
          const predStack = this.stackAtExit.get(predId);
          if (predStack && stackSlot < predStack.length) {
            phi.inputs[inputIndex] = { blockId: predId, value: predStack[stackSlot] };
          }
        } else {
          const predLocals = this.localVersions.get(predId);
          if (predLocals && predLocals.has(localIndex)) {
            phi.inputs[inputIndex] = { blockId: predId, value: predLocals.get(localIndex)! };
          }
        }
      }
    }
  }

  // ─── Instruction translation ───

  private translateInstruction(instruction: DecodedInstruction): void {
    const opCode = instruction.opCode;
    if (this.isStructuralMarker(opCode)) { return; }
    if (this.translateAccess(instruction, opCode)) { return; }
    if (this.translateArithmetic(instruction, opCode)) { return; }
    if (this.translateMemory(instruction, opCode)) { return; }
    if (this.translateCall(instruction, opCode)) { return; }
    if (this.translateBranchAndMisc(instruction, opCode)) { return; }
    if (this.translateMemoryControl(opCode)) { return; }
    this.translateIntrinsic(instruction, opCode);
  }

  private isStructuralMarker(opCode: OpCodeDef): boolean {
    return opCode === OpCodes.nop || opCode === OpCodes.block || opCode === OpCodes.loop || opCode === OpCodes.end ||
      opCode === OpCodes.else || opCode === OpCodes.try || opCode === OpCodes.catch || opCode === OpCodes.catch_all ||
      opCode === OpCodes.delegate;
  }

  private translateAccess(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    if (opCode === OpCodes.if) {
      const condition = this.stack.pop();
      if (condition && this.cfgBlock.successors.length >= 2) {
        this.ssaBlock.instructions.push({ kind: 'branch_if', condition, trueTarget: this.cfgBlock.successors[0].id, falseTarget: this.cfgBlock.successors[1].id });
      }
      return true;
    }
    const immediate = instruction.immediates;
    if (opCode === OpCodes.get_local && immediate.type === 'Local') {
      const localIndex = immediate.localIndex;
      this.stack.push(this.currentLocals.get(localIndex) || this.newVariable(`local${localIndex}`, this.getLocalType(localIndex), this.cfgBlock.id));
      return true;
    }
    if ((opCode === OpCodes.set_local || opCode === OpCodes.tee_local) && immediate.type === 'Local') {
      this.assignLocal(immediate.localIndex, opCode === OpCodes.tee_local);
      return true;
    }
    if (opCode === OpCodes.get_global && immediate.type === 'Global') {
      const globalIndex = immediate.globalIndex;
      const result = this.newVariable(`g${globalIndex}_v${this.variableCounter}`, this.getGlobalType(globalIndex), this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'global_get', result, globalIndex });
      this.stack.push(result);
      return true;
    }
    if (opCode === OpCodes.set_global && immediate.type === 'Global') {
      const globalIndex = immediate.globalIndex;
      const value = this.stack.pop();
      if (value) { this.ssaBlock.instructions.push({ kind: 'global_set', globalIndex, value }); }
      return true;
    }
    if (immediate.type === 'VarInt32' || immediate.type === 'VarInt64' || immediate.type === 'Float32' || immediate.type === 'Float64') {
      const constType = resultTypeFromOpCode(opCode);
      const result = this.newVariable(`c${this.variableCounter}`, constType, this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'const', result, value: immediate.value, type: constType });
      this.stack.push(result);
      return true;
    }
    return false;
  }

  private assignLocal(localIndex: number, keepOnStack: boolean): void {
    const value = this.stack.pop();
    if (!value) { return; }
    const newVar = this.newVariable(`local${localIndex}_v${this.variableCounter}`, this.getLocalType(localIndex), this.cfgBlock.id);
    this.ssaBlock.instructions.push({ kind: 'assign', result: newVar, value });
    this.currentLocals.set(localIndex, newVar);
    if (keepOnStack) { this.stack.push(newVar); }
  }

  private translateArithmetic(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    const binaryOp = BINARY_OPS.get(opCode);
    if (binaryOp) {
      return this.emitBinary(binaryOp, resultTypeFromOpCode(opCode));
    }
    const compareOp = COMPARE_OPS.get(opCode);
    if (compareOp) {
      const right = this.stack.pop();
      const left = this.stack.pop();
      if (left && right) {
        const result = this.newVariable(`t${this.variableCounter}`, 'i32', this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'compare', result, op: compareOp, left, right });
        this.stack.push(result);
      }
      return true;
    }
    if (opCode === OpCodes.i32_eqz || opCode === OpCodes.i64_eqz) {
      return this.emitUnary('!', 'i32');
    }
    const unaryOp = UNARY_OPS.get(opCode);
    if (unaryOp) {
      return this.emitUnary(unaryOp, resultTypeFromOpCode(opCode));
    }
    const conversionOp = CONVERSION_OPS.get(opCode);
    if (conversionOp) {
      const operand = this.stack.pop();
      if (operand) {
        const result = this.newVariable(`t${this.variableCounter}`, resultTypeFromOpCode(opCode), this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'convert', result, op: conversionOp, operand });
        this.stack.push(result);
      }
      return true;
    }
    return false;
  }

  private emitBinary(op: string, resultType: string): boolean {
    const right = this.stack.pop();
    const left = this.stack.pop();
    if (left && right) {
      const result = this.newVariable(`t${this.variableCounter}`, resultType, this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'binary', result, op, left, right });
      this.stack.push(result);
    }
    return true;
  }

  private emitUnary(op: string, resultType: string): boolean {
    const operand = this.stack.pop();
    if (operand) {
      const result = this.newVariable(`t${this.variableCounter}`, resultType, this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'unary', result, op, operand });
      this.stack.push(result);
    }
    return true;
  }

  private translateMemory(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    if (opCode.mnemonic.includes('atomic.rmw')) {
      this.translateAtomicRmw(instruction, opCode);
      return true;
    }
    if (opCode.mnemonic.includes('atomic.notify') || opCode.mnemonic.includes('atomic.wait')) {
      if (opCode.mnemonic.includes('wait')) { this.stack.pop(); }   // timeout operand
      this.stack.pop();                                             // value/expected operand
      const address = this.stack.pop();
      if (address) {
        const result = this.newVariable(`t${this.variableCounter}`, 'i32', this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'load', result, address, offset: 0, loadType: opCode.mnemonic });
        this.stack.push(result);
      }
      return true;
    }
    if (opCode.immediate === 'MemoryImmediate' && opCode.mnemonic.includes('load')) {
      const offset = instruction.immediates.type === 'MemoryImmediate' ? instruction.immediates.offset : 0;
      const address = this.stack.pop();
      if (address) {
        const result = this.newVariable(`t${this.variableCounter}`, resultTypeFromOpCode(opCode), this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'load', result, address, offset, loadType: opCode.mnemonic });
        this.stack.push(result);
      }
      return true;
    }
    if (opCode.immediate === 'MemoryImmediate' && opCode.mnemonic.includes('store')) {
      const offset = instruction.immediates.type === 'MemoryImmediate' ? instruction.immediates.offset : 0;
      const value = this.stack.pop();
      const address = this.stack.pop();
      if (address && value) {
        this.ssaBlock.instructions.push({ kind: 'store', address, value, offset, storeType: opCode.mnemonic });
      }
      return true;
    }
    return false;
  }

  private translateAtomicRmw(instruction: DecodedInstruction, opCode: OpCodeDef): void {
    const offset = instruction.immediates.type === 'MemoryImmediate' ? instruction.immediates.offset : 0;
    if (!opCode.mnemonic.includes('cmpxchg')) {
      this.stack.pop();   // value operand (consumed but modeled as a load)
      const address = this.stack.pop();
      if (address) {
        const result = this.newVariable(`t${this.variableCounter}`, resultTypeFromOpCode(opCode), this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'load', result, address, offset, loadType: opCode.mnemonic });
        this.stack.push(result);
      }
      return;
    }
    const replacement = this.stack.pop();
    const expected = this.stack.pop();
    const address = this.stack.pop();
    if (!address || !expected || !replacement) { return; }
    const result = this.newVariable(`t${this.variableCounter}`, resultTypeFromOpCode(opCode), this.cfgBlock.id);
    let effectiveAddress: SsaValue = address;
    if (offset > 0) {
      const offsetVar = this.newVariable(`t${this.variableCounter}`, 'i32', this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'binary', result: offsetVar, op: '+', left: address, right: { kind: 'const', value: offset, type: 'i32' } });
      effectiveAddress = offsetVar;
    }
    this.ssaBlock.instructions.push({ kind: 'call', result, target: CallTarget.AtomicCompareExchange, args: [effectiveAddress, expected, replacement] });
    this.stack.push(result);
  }

  private translateCall(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    if ((opCode === OpCodes.call || opCode === OpCodes.return_call) && instruction.immediates.type === 'Function') {
      const targetIndex = instruction.immediates.functionIndex;
      const targetType = this.getCallTargetType(targetIndex);
      if (targetType) {
        const args = this.popArgs(targetType.parameterTypes.length);
        const result = this.pushCallResults(targetType.returnTypes);
        this.ssaBlock.instructions.push({ kind: 'call', result, target: targetIndex, args });
      }
      return true;
    }
    if ((opCode === OpCodes.call_indirect || opCode === OpCodes.return_call_indirect) && instruction.immediates.type === 'IndirectFunction') {
      const typeIdx = instruction.immediates.typeIndex;
      const tableIndex = this.stack.pop();
      const calleeType = typeIdx < this.flatTypes.length ? this.flatTypes[typeIdx] : null;
      if (calleeType && calleeType.kind === 'func' && tableIndex) {
        const args = this.popArgs(calleeType.parameterTypes.length);
        const result = this.pushCallResults(calleeType.returnTypes);
        this.ssaBlock.instructions.push({ kind: 'call_indirect', result, tableIndex, typeIndex: typeIdx, args });
      }
      return true;
    }
    return false;
  }

  private popArgs(count: number): SsaValue[] {
    const args: SsaValue[] = [];
    for (let argIndex = 0; argIndex < count; argIndex++) {
      const arg = this.stack.pop();
      if (arg) { args.unshift(arg); }
    }
    return args;
  }

  // Pushes the first result and, for multi-value returns, extra copies to keep the stack balanced.
  private pushCallResults(returnTypes: (number | { name: string })[]): SsaVariable | null {
    if (returnTypes.length === 0) { return null; }
    const result = this.newVariable(`t${this.variableCounter}`, wasmTypeStr(returnTypes[0]), this.cfgBlock.id);
    this.stack.push(result);
    for (let retIdx = 1; retIdx < returnTypes.length; retIdx++) {
      const extraResult = this.newVariable(`t${this.variableCounter}`, wasmTypeStr(returnTypes[retIdx]), this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'assign', result: extraResult, value: result });
      this.stack.push(extraResult);
    }
    return result;
  }

  private translateBranchAndMisc(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    if (opCode === OpCodes.drop) { this.stack.pop(); return true; }
    if (opCode === OpCodes.select) {
      const condition = this.stack.pop();
      const falseVal = this.stack.pop();
      const trueVal = this.stack.pop();
      if (condition && falseVal && trueVal) {
        const selectType = ('type' in trueVal && typeof trueVal.type === 'string') ? trueVal.type : 'i32';
        const result = this.newVariable(`t${this.variableCounter}`, selectType, this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'select', result, condition, trueVal, falseVal });
        this.stack.push(result);
      }
      return true;
    }
    if (opCode === OpCodes.br) {
      const targetBlock = this.cfgBlock.successors[0];
      if (targetBlock) { this.ssaBlock.instructions.push({ kind: 'branch', target: targetBlock.id }); }
      return true;
    }
    if (opCode === OpCodes.br_if) {
      const condition = this.stack.pop();
      if (condition && this.cfgBlock.successors.length >= 2) {
        this.ssaBlock.instructions.push({ kind: 'branch_if', condition, trueTarget: this.cfgBlock.successors[0].id, falseTarget: this.cfgBlock.successors[1].id });
      }
      return true;
    }
    if (opCode === OpCodes.br_table) {
      this.translateBrTable();
      return true;
    }
    if (opCode === OpCodes.return) {
      this.ssaBlock.instructions.push({ kind: 'return', values: this.popArgs(this.funcType.returnTypes.length) });
      return true;
    }
    if (opCode === OpCodes.unreachable || opCode === OpCodes.throw || opCode === OpCodes.rethrow) {
      this.ssaBlock.instructions.push({ kind: 'unreachable' });
      return true;
    }
    return false;
  }

  private translateBrTable(): void {
    const selector = this.stack.pop();
    if (!selector) { return; }
    if (this.cfgBlock.brTableTargets && this.cfgBlock.brTableDefault) {
      const targets = this.cfgBlock.brTableTargets.map(target => target.id);
      this.ssaBlock.instructions.push({ kind: 'branch_table', selector, targets, defaultTarget: this.cfgBlock.brTableDefault.id });
    } else {
      const targets = this.cfgBlock.successors.slice(0, -1).map(successor => successor.id);
      const defaultTarget = this.cfgBlock.successors[this.cfgBlock.successors.length - 1]?.id || 0;
      this.ssaBlock.instructions.push({ kind: 'branch_table', selector, targets, defaultTarget });
    }
  }

  private translateMemoryControl(opCode: OpCodeDef): boolean {
    if (opCode === OpCodes.mem_size) {
      const result = this.newVariable(`t${this.variableCounter}`, 'i32', this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'call', result, target: CallTarget.MemorySize, args: [] });
      this.stack.push(result);
      return true;
    }
    if (opCode === OpCodes.mem_grow) {
      const pages = this.stack.pop();
      const result = this.newVariable(`t${this.variableCounter}`, 'i32', this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'call', result, target: CallTarget.MemoryGrow, args: pages ? [pages] : [] });
      this.stack.push(result);
      return true;
    }
    return false;
  }

  private translateIntrinsic(instruction: DecodedInstruction, opCode: OpCodeDef): void {
    if (this.translateGcOp(instruction, opCode)) { return; }
    if (this.translateTableBulkOp(instruction, opCode)) { return; }
    if (this.translateSimdTernary(opCode)) { return; }
    if (this.translateShuffle(instruction, opCode)) { return; }
    if (this.translateSimdBinaryOrUnary(opCode)) { return; }
    this.translateGenericStack(opCode);
  }

  private translateGcOp(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    const mnemonic = opCode.mnemonic;
    const isGcOp = mnemonic.startsWith('struct.') || mnemonic.startsWith('array.') ||
      mnemonic === 'ref.i31' || mnemonic.startsWith('i31.') || mnemonic.startsWith('ref.test') || mnemonic.startsWith('ref.cast') ||
      mnemonic.startsWith('any.') || mnemonic.startsWith('extern.') ||
      mnemonic === 'ref.null' || mnemonic === 'ref.is_null' || mnemonic === 'ref.func';
    if (!isGcOp) { return false; }
    let popCount = opCode.popOperands ? opCode.popOperands.length : 0;
    const pushCount = opCode.pushOperands ? opCode.pushOperands.length : 0;
    const immediateNumberList = immediateNumbers(instruction.immediates);
    if (mnemonic === 'struct.new' && immediateNumberList.length > 0) {
      const typeIndex = immediateNumberList[0];
      const flatTypes = flattenTypes(this.moduleInfo);
      if (typeIndex < flatTypes.length && flatTypes[typeIndex].kind === 'struct') {
        popCount = (flatTypes[typeIndex] as { fields: unknown[] }).fields.length;
      }
    }
    const args = this.popArgs(popCount);
    this.appendGcImmediates(instruction, mnemonic, args);
    const resultType = /v128|x4|x8|x16|x2/.test(mnemonic) ? 'v128' : 'i32';
    this.emitIntrinsic(mnemonic, args, pushCount > 0, resultType);
    return true;
  }

  // GC immediates become const args, except the leading type-index (an internal WASM index).
  private appendGcImmediates(instruction: DecodedInstruction, mnemonic: string, args: SsaValue[]): void {
    const isGcOp = mnemonic.startsWith('struct.') || mnemonic.startsWith('array.') || mnemonic.startsWith('ref.') ||
      mnemonic.startsWith('i31.') || mnemonic.startsWith('any.') || mnemonic.startsWith('extern.');
    const immediateValues = immediateNumbers(instruction.immediates);
    for (let immIdx = 0; immIdx < immediateValues.length; immIdx++) {
      if (isGcOp && immIdx === 0) { continue; }
      args.push({ kind: 'const', value: immediateValues[immIdx], type: 'i32' });
    }
  }

  private translateTableBulkOp(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    const mnemonic = opCode.mnemonic;
    const isTableBulk = mnemonic.startsWith('table.') || mnemonic === 'memory.fill' || mnemonic === 'memory.copy' ||
      mnemonic === 'memory.init' || mnemonic === 'data.drop' || mnemonic === 'elem.drop' || mnemonic === 'table.init' || mnemonic === 'table.copy';
    if (!isTableBulk) { return false; }
    const args = this.popArgs(opCode.popOperands ? opCode.popOperands.length : 0);
    for (const immVal of immediateNumbers(instruction.immediates)) {
      args.push({ kind: 'const', value: immVal, type: 'i32' });
    }
    this.emitIntrinsic(mnemonic, args, (opCode.pushOperands ? opCode.pushOperands.length : 0) > 0, 'i32');
    return true;
  }

  private emitIntrinsic(mnemonic: string, args: SsaValue[], pushesResult: boolean, resultType: string): void {
    const intrinsicName = mnemonic.replace(/\./g, '_');
    if (pushesResult) {
      const result = this.newVariable(`t${this.variableCounter}`, resultType, this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'call', result, target: CallTarget.ResultIntrinsic, args });
      this.intrinsicNames.set(result.id, intrinsicName);
      this.stack.push(result);
    } else {
      const voidId = this.voidIntrinsicCounter--;
      this.intrinsicNames.set(voidId, intrinsicName);
      this.ssaBlock.instructions.push({ kind: 'call', result: null, target: voidId, args });
    }
  }

  // SIMD ternary ops (pop 3, push 1) — checked before the binary handler below.
  private translateSimdTernary(opCode: OpCodeDef): boolean {
    if (opCode.stackBehavior !== 'PopPush' || !opCode.popOperands || opCode.popOperands.length !== 3) { return false; }
    const arg3 = this.stack.pop();
    const arg2 = this.stack.pop();
    const arg1 = this.stack.pop();
    if (arg1 && arg2 && arg3) {
      const result = this.newVariable(`t${this.variableCounter}`, 'v128', this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'call', result, target: CallTarget.ResultIntrinsic, args: [arg1, arg2, arg3] });
      this.intrinsicNames.set(result.id, opCode.mnemonic.replace(/\./g, '_'));
      this.stack.push(result);
    }
    return true;
  }

  private translateShuffle(instruction: DecodedInstruction, opCode: OpCodeDef): boolean {
    if (opCode.mnemonic !== 'i8x16.shuffle') { return false; }
    const right = this.stack.pop();
    const left = this.stack.pop();
    if (left && right) {
      const args: SsaValue[] = [left, right];
      if (instruction.immediates.type === 'ShuffleMask') {
        for (const byte of instruction.immediates.mask) { args.push({ kind: 'const', value: byte, type: 'i32' }); }
      }
      const result = this.newVariable(`t${this.variableCounter}`, 'v128', this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'call', result, target: CallTarget.ResultIntrinsic, args });
      this.intrinsicNames.set(result.id, 'i8x16_shuffle');
      this.stack.push(result);
    }
    return true;
  }

  private translateSimdBinaryOrUnary(opCode: OpCodeDef): boolean {
    if (opCode.stackBehavior !== 'PopPush') { return false; }
    const mnemonic = opCode.mnemonic;
    const resultType = mnemonic.startsWith('v128') || mnemonic.includes('x') ? 'v128' : resultTypeFromOpCode(opCode);
    if (this.isSimdBinaryMnemonic(mnemonic)) {
      const right = this.stack.pop();
      const left = this.stack.pop();
      if (left && right) {
        const result = this.newVariable(`t${this.variableCounter}`, resultType, this.cfgBlock.id);
        this.ssaBlock.instructions.push({ kind: 'binary', result, op: mnemonic.replace(/\./g, '_'), left, right });
        this.stack.push(result);
      }
      return true;
    }
    // Remaining PopPush: treat as unary (convert/extend/trunc/etc.).
    const operand = this.stack.pop();
    if (operand) {
      const result = this.newVariable(`t${this.variableCounter}`, resultType, this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'convert', result, op: mnemonic.replace(/\./g, '_'), operand });
      this.stack.push(result);
    }
    return true;
  }

  private isSimdBinaryMnemonic(mnemonic: string): boolean {
    return mnemonic.includes('.add') || mnemonic.includes('.sub') || mnemonic.includes('.mul') ||
      mnemonic.includes('.and') || mnemonic.includes('.or') || mnemonic.includes('.xor') ||
      mnemonic.includes('.shl') || mnemonic.includes('.shr') || mnemonic.includes('.min') || mnemonic.includes('.max') ||
      mnemonic.includes('.eq') || (mnemonic.includes('.ne') && !mnemonic.includes('.neg') && !mnemonic.includes('.nearest')) ||
      mnemonic.includes('.lt') || mnemonic.includes('.gt') || mnemonic.includes('.le') || mnemonic.includes('.ge') ||
      mnemonic.includes('.avgr') || mnemonic.includes('.swizzle') || mnemonic.includes('.narrow') || mnemonic.includes('.dot') ||
      mnemonic.includes('.q15mulr') || mnemonic.includes('.extmul') || mnemonic.includes('.bitselect') || mnemonic.includes('.andnot');
  }

  private translateGenericStack(opCode: OpCodeDef): void {
    if (opCode.stackBehavior === 'Push') {
      const resultType = opCode.mnemonic.includes('v128') ? 'v128' : 'i32';
      const result = this.newVariable(`t${this.variableCounter}`, resultType, this.cfgBlock.id);
      this.ssaBlock.instructions.push({ kind: 'const', result, value: 0, type: resultType });
      this.stack.push(result);
    } else if (opCode.stackBehavior === 'Pop') {
      this.stack.pop();
    }
  }
}

function computeReversePostOrder(cfg: ControlFlowGraph): BasicBlock[] {
  const visited = new Set<number>();
  const order: BasicBlock[] = [];
  const blockStack: { block: BasicBlock; childIndex: number }[] = [{ block: cfg.entry, childIndex: 0 }];
  visited.add(cfg.entry.id);

  while (blockStack.length > 0) {
    const frame = blockStack[blockStack.length - 1];
    if (frame.childIndex < frame.block.successors.length) {
      const child = frame.block.successors[frame.childIndex];
      frame.childIndex++;
      if (!visited.has(child.id)) {
        visited.add(child.id);
        blockStack.push({ block: child, childIndex: 0 });
      }
    } else {
      order.push(frame.block);
      blockStack.pop();
    }
  }

  order.reverse();
  return order;
}
